import { Link, Outlet, useLocation } from "react-router-dom";
import {
  Home, ClipboardList, PlusCircle, FileText, BarChart3, Settings,
  PawPrint, LogOut, HelpCircle, LayoutTemplate, FlaskConical,
} from "lucide-react";
import { TestModeProvider, useTestMode } from "./TestModeContext";
import { useAdminAuth } from "./AdminAuthContext";

/**
 * Persistent admin chrome (sidebar) shared across every /admin route.
 * Nested route content renders inside <Outlet />.
 * Sidebar visuals mirror the existing PawSomeDashboard chrome so the
 * admin experience is uniform.
 */

const NAV_ITEMS = [
  { label: "Dashboard",        icon: Home,           to: "/admin",                  match: (p) => p === "/admin" },
  { label: "Orders",           icon: ClipboardList,  to: "/admin/orders",           match: (p) => p.startsWith("/admin/orders") },
  { label: "New Order",        icon: PlusCircle,     to: "/admin",                  match: () => false },
  { label: "Templates",        icon: FileText,       to: "/admin",                  match: () => false },
  { label: "Template Overlay", icon: LayoutTemplate, to: "/admin/template-overlay", match: (p) => p.startsWith("/admin/template-overlay") },
  { label: "Reports",          icon: FileText,       to: "/admin",                  match: () => false },
  { label: "Analytics",        icon: BarChart3,      to: "/admin",                  match: () => false },
  { label: "Settings",         icon: Settings,       to: "/admin",                  match: () => false },
];

export default function AdminLayout() {
  return (
    <TestModeProvider>
      <AdminLayoutInner />
    </TestModeProvider>
  );
}

function AdminLayoutInner() {
  const { pathname } = useLocation();
  const { testMode, loading: testLoading, toggle: toggleTestMode } = useTestMode();
  const { logout } = useAdminAuth();

  return (
    <div
      style={{
        display: "flex", minHeight: "100vh", background: "#F7F6FB",
        fontFamily: "'Nunito', sans-serif", color: "#2A2540",
      }}
      data-testid="admin-layout"
    >
      <aside
        style={{
          width: 232, background: "#fff", borderRight: "1px solid #ECEAF3",
          display: "flex", flexDirection: "column", padding: "22px 16px",
          position: "sticky", top: 0, height: "100vh",
        }}
        data-testid="admin-sidebar"
      >
        <div style={{ paddingLeft: 8, marginBottom: 26 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PawPrint size={26} color="#7A5CD0" fill="#7A5CD0" />
            <span style={{ fontFamily: "'Pacifico', cursive", fontSize: 24, color: "#6A4FC0" }}>Paw-Some</span>
          </div>
          <div style={{ fontSize: 12, letterSpacing: 3, color: "#9A93B0", fontWeight: 700, marginLeft: 34 }}>REPORTS</div>
          <div style={{ fontSize: 11, fontStyle: "italic", color: "#B0AAC0", marginTop: 6, marginLeft: 2 }}>Because every pet has a story.</div>
        </div>

        <nav style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          {NAV_ITEMS.map(({ label, icon: Icon, to, match }) => {
            const active = match(pathname);
            return (
              <Link
                key={label}
                to={to}
                data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px", borderRadius: 11, textDecoration: "none",
                  background: active ? "#EEE9FB" : "transparent",
                  color: active ? "#6A4FC0" : "#6E6880",
                  fontWeight: active ? 700 : 600, fontSize: 14.5,
                }}
              >
                <Icon size={19} />{label}
              </Link>
            );
          })}
        </nav>

        <TestModeSwitch testMode={testMode} loading={testLoading} onToggle={toggleTestMode} />

        <div style={{ background: "#F3F0FB", borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <HelpCircle size={18} color="#7A5CD0" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Need Help?</div>
              <div style={{ fontSize: 12, color: "#7A5CD0", fontWeight: 700 }}>Production Manual</div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid #EFEDF5", paddingTop: 14 }}>
          <div style={{ width: 34, height: 34, borderRadius: 999, background: "#E3DBF7", display: "grid", placeItems: "center" }}>
            <PawPrint size={17} color="#7A5CD0" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Val</div>
            <div style={{ fontSize: 12, color: "#9A93B0" }}>Administrator</div>
          </div>
          <button type="button" onClick={logout} data-testid="admin-logout" title="Log out" aria-label="Log out" style={{ border: 0, background: "transparent", padding: 5, color: "#7A728D", cursor: "pointer", display: "grid", placeItems: "center" }}>
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0 }}>
        {testMode && <TestModeBanner />}
        <Outlet />
      </div>
    </div>
  );
}

function TestModeBanner() {
  return (
    <div
      data-testid="test-mode-banner"
      style={{
        background: "linear-gradient(90deg, #FDE68A 0%, #FCD34D 100%)",
        color: "#78350F",
        padding: "10px 24px",
        fontSize: 13,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        borderBottom: "1px solid #FBBF24",
      }}
    >
      <FlaskConical size={15} />
      TEST MODE IS ON — new orders are tagged as test orders and will not be treated as live production.
    </div>
  );
}

function TestModeSwitch({ testMode, loading, onToggle }) {
  const on = !!testMode;
  return (
    <button
      type="button"
      data-testid="test-mode-toggle"
      aria-pressed={on}
      disabled={loading}
      onClick={() => onToggle(!on)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", marginBottom: 12,
        borderRadius: 12, border: "1px solid " + (on ? "#FCD34D" : "#ECEAF3"),
        background: on ? "#FEF3C7" : "#FAFAFF",
        color: on ? "#78350F" : "#6E6880",
        cursor: loading ? "wait" : "pointer",
        textAlign: "left", fontFamily: "inherit",
      }}
    >
      <FlaskConical size={17} color={on ? "#B45309" : "#9A93B0"} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800 }}>Test Mode</div>
        <div data-testid="test-mode-state" style={{ fontSize: 11, fontWeight: 700, opacity: 0.8 }}>
          {on ? "ON — tagging test orders" : "OFF — live production"}
        </div>
      </div>
      <span
        aria-hidden
        style={{
          width: 32, height: 18, borderRadius: 999,
          background: on ? "#B45309" : "#D6D2E4",
          position: "relative", flexShrink: 0,
          transition: "background 150ms ease",
        }}
      >
        <span style={{
          position: "absolute", top: 2, left: on ? 16 : 2,
          width: 14, height: 14, borderRadius: 999, background: "#fff",
          transition: "left 150ms ease",
        }} />
      </span>
    </button>
  );
}
